import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import AdBanner from './AdBanner';
import AdExpanded from './AdExpanded';
import {
  isNativeAndroidApp,
  loadAds,
  showAdMobInterstitial,
  trackAdEvent,
} from '../services/adsService';
import {
  getNextEligibleAdTime,
  getSessionData,
  getSessionDuration,
  markAdClicked,
  markAdDismissed,
  markAdShown,
  resetAdSessionData,
  shouldShowAds,
} from '../services/adManagerService';
import { trackAnalyticsEvent } from '../services/analyticsService';

const ROTATION_MS = 11000;
const INTERACTION_COOLDOWN_MS = 1000 * 30;

function AdManager({ isAuthenticated, user }) {
  const location = useLocation();
  const [ads, setAds] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [paused, setPaused] = useState(false);
  const [lastInteractionAt, setLastInteractionAt] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [sessionData, setSessionData] = useState(() => getSessionData());
  const previousSubscriptionStatusRef = useRef(null);

  const inDashboard = isAuthenticated && location.pathname.startsWith('/dashboard');
  const shouldShow = useMemo(() => {
    if (!inDashboard) return false;
    if (!subscriptionChecked) return false;
    return shouldShowAds({ user, subscriptionStatus, sessionData });
  }, [inDashboard, user, subscriptionStatus, sessionData, subscriptionChecked]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;

    axios.get('/subscription/status')
      .then((res) => {
        if (!mounted) return;
        const status = res?.data?.status || null;
        setSubscriptionStatus(status);
        setSubscriptionChecked(true);
      })
      .catch(() => {
        if (!mounted) return;
        setSubscriptionStatus(null);
        setSubscriptionChecked(true);
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const previous = previousSubscriptionStatusRef.current;
    if (subscriptionStatus === 'ACTIVE' && previous && previous !== 'ACTIVE') {
      trackAnalyticsEvent('subscription_activated', { from_status: previous, to_status: 'ACTIVE' });
    }
    if (previous === 'ACTIVE' && subscriptionStatus && subscriptionStatus !== 'ACTIVE') {
      setSessionData(resetAdSessionData());
      trackAnalyticsEvent('subscription_deactivated_ads_reenabled', {
        from_status: previous,
        to_status: subscriptionStatus,
      });
    }
    previousSubscriptionStatusRef.current = subscriptionStatus;
  }, [subscriptionStatus]);

  useEffect(() => {
    if (!shouldShow) return;
    let mounted = true;

    loadAds().then((loaded) => {
      if (!mounted) return;
      const sanitized = Array.isArray(loaded) ? loaded.filter((ad) => ad?.id && ad?.title) : [];
      setAds(sanitized);
    });

    return () => {
      mounted = false;
    };
  }, [shouldShow]);

  const activeAd = useMemo(() => {
    if (!ads.length) return null;
    return ads[Math.max(0, activeIndex % ads.length)];
  }, [ads, activeIndex]);

  useEffect(() => {
    if (!activeAd || !shouldShow) return;
    trackAdEvent('impression', activeAd);
    setSessionData((previous) => {
      const nextSession = markAdShown(previous);
      trackAnalyticsEvent('ad_shown', {
        ad_id: activeAd.id,
        session_duration_ms: getSessionDuration(nextSession),
      });
      return nextSession;
    });
  }, [activeAd, shouldShow]);

  useEffect(() => {
    if (!ads.length || paused || isExpanded || !shouldShow) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % ads.length);
    }, ROTATION_MS);

    return () => window.clearInterval(timer);
  }, [ads.length, paused, isExpanded, shouldShow]);

  if (!shouldShow || !activeAd) return null;

  const canInteract = Date.now() - lastInteractionAt > INTERACTION_COOLDOWN_MS;

  const openExpanded = async () => {
    if (!canInteract) return;
    setLastInteractionAt(Date.now());
    await trackAdEvent('click', activeAd);
    setIsExpanded(true);
  };

  const handleCta = async () => {
    if (!canInteract) return;
    setLastInteractionAt(Date.now());
    await trackAdEvent('cta', activeAd);
    setSessionData((previous) => {
      const nextSession = markAdClicked(previous);
      trackAnalyticsEvent('ad_clicked', {
        ad_id: activeAd.id,
        ad_clicks_count: nextSession.adClicksCount,
      });
      return nextSession;
    });

    if (activeAd.provider === 'google_admob' && isNativeAndroidApp()) {
      const opened = await showAdMobInterstitial(activeAd.adUnitId);
      if (opened) return;
    }

    if (activeAd.ctaUrl) {
      window.open(activeAd.ctaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <div className="h-[76px]" aria-hidden="true" />
      <AdBanner
        ads={ads}
        activeAd={activeAd}
        onExpand={openExpanded}
        paused={paused}
        onTogglePaused={() => setPaused((prev) => !prev)}
      />

      {isExpanded && (
        <AdExpanded
          ad={activeAd}
          onOpenLink={handleCta}
          onCollapse={() => setIsExpanded(false)}
          onClose={() => {
            setIsExpanded(false);
            setSessionData((previous) => {
              const nextSession = markAdDismissed(previous);
              trackAnalyticsEvent('ad_dismissed', {
                ad_id: activeAd.id,
                next_eligible_at: getNextEligibleAdTime(nextSession),
              });
              return nextSession;
            });
          }}
        />
      )}
    </>
  );
}

export default AdManager;
